import React, { useState, useMemo, useEffect, useRef } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { fetchUserName } from "../../../../redux/slices/users";
import { setColor } from "../../../../assets/styles/styles";
import MetricCard from "./components/MetricCard";
import Barchart from "./components/Barchart";
import AvatarBarchart from "./components/AvatarChart";
import HorizontalBarChart from "./components/HorizontalBarChart";
import FilterIcon from "../../../../assets/images/icons/Web_Sprite_Icon_Filter.png";
import Chevron from "../../../../assets/images/icons/Web_Sprite_Icon_Chevron_Grey_analytics.png";
import EditIcon from "../../../../assets/images/icons/Web_Sprite_Icon_Edit_Grey.png";
import LinkoutList from "./components/LinkoutList";
import Piechart from "./components/Piechart";
import DateFilter from "./components/DateFilter";
import ClientTagFilter from "./components/ClientTagFilter";
import CustomDatePicker from "./components/CustomDatePicker";
import Modal from "./components/Modal";
import {
    getTotalCountData,
    getCategoriesData,
    getBrandsData,
    getClientTags,
    getInteractionData,
    getLinkoutsData,
    getDevicesData,
    getAvatarsData,
    getRoomOwnersByClientTag,
} from "../../../../api/AnalyticsController";
import { CACHE_QUERY_OPTIONS } from "../../../../constants";
import LoadingIcon from "../../../../components/common/loading/LoadingIcon";
import { getDateRange } from "../../../../utils/generalUtils";

const useDateRange = (initialRange = "Last 90 days") => {
    const [selectedDate, setSelectedDate] = useState(initialRange);
    const [customDateRange, setCustomDateRange] = useState({ startDate: null, endDate: null });
    const displayDate = useMemo(() => {
        if (selectedDate === "Custom" && customDateRange.startDate && customDateRange.endDate) {
            return `${format(customDateRange.startDate, "MMM dd, yyyy")} to ${format(customDateRange.endDate, "MMM dd, yyyy")}`;
        }
        return selectedDate;
    }, [selectedDate, customDateRange]);

    return { selectedDate, setSelectedDate, customDateRange, setCustomDateRange, displayDate };
};

const Analytics = () => {
    const user = useSelector((state) => state.user);
    const username = useSelector(fetchUserName);
    const dropdownRef = useRef(null);
    const { selectedDate, setSelectedDate, customDateRange, setCustomDateRange, displayDate } = useDateRange();
    // const [pdfDates, setPdfDates] = useState(() => getDateRange(selectedDate, customDateRange));

    // useEffect(() => {
    //     setPdfDates(getDateRange(selectedDate, customDateRange));
    // }, [selectedDate, customDateRange]);

    // const { pdfStartDate, pdfEndDate } = pdfDates;
    const pdfDates = useMemo(() => {
        const now = new Date();
        const nowUTC = new Date(
            Date.UTC(
                now.getUTCFullYear(),
                now.getUTCMonth(),
                now.getUTCDate(),
                now.getUTCHours(),
                now.getUTCMinutes(),
                now.getUTCSeconds(),
                now.getUTCMilliseconds()
            )
        );

        let startDateUTC;
        let endDateUTC = nowUTC;

        if (selectedDate === "Custom" && customDateRange.startDate && customDateRange.endDate) {
            startDateUTC = new Date(
                Date.UTC(
                    customDateRange.startDate.getUTCFullYear(),
                    customDateRange.startDate.getUTCMonth(),
                    customDateRange.startDate.getUTCDate(),
                    0,
                    0,
                    0,
                    0
                )
            );
            endDateUTC = new Date(
                Date.UTC(
                    customDateRange.endDate.getUTCFullYear(),
                    customDateRange.endDate.getUTCMonth(),
                    customDateRange.endDate.getUTCDate(),
                    23,
                    59,
                    59,
                    999
                )
            );
        } else {
            switch (selectedDate) {
                case "Last 24 hours":
                    startDateUTC = new Date(nowUTC.getTime() - 24 * 60 * 60 * 1000);
                    break;
                case "Last 7 days":
                    startDateUTC = new Date(nowUTC);
                    startDateUTC.setUTCDate(startDateUTC.getUTCDate() - 6);
                    startDateUTC.setUTCHours(0, 0, 0, 0);
                    break;
                case "Last 30 days":
                    startDateUTC = new Date(nowUTC);
                    startDateUTC.setUTCDate(startDateUTC.getUTCDate() - 29);
                    startDateUTC.setUTCHours(0, 0, 0, 0);
                    break;
                case "Last 90 days":
                default:
                    startDateUTC = new Date(nowUTC);
                    startDateUTC.setUTCDate(startDateUTC.getUTCDate() - 89);
                    startDateUTC.setUTCHours(0, 0, 0, 0);
            }
        }

        return { pdfStartDate: startDateUTC, pdfEndDate: endDateUTC };
    }, [selectedDate, customDateRange]);

    const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);
    const [isCategoryFilterOpen, setIsCategoryFilterOpen] = useState(false);
    const [showCustomPicker, setShowCustomPicker] = useState(false);

    const [isDropdownOpen, setDropdownOpen] = React.useState(false);
    const [CategoryData, setCategoryData] = useState([]);
    const [InteractionData, setInteractionData] = useState([]);
    const [LinkoutData, setLinkoutData] = useState([]);
    const [DevicesData, setDevicesData] = useState([]);
    const [OSData, setOSData] = useState([]);
    const [BrowsersData, setBrowsersData] = useState([]);
    const [AvatarsData, setAvatarsData] = useState([]);

    /// for pop up modal charts
    const [isBrandBarChartModalOpen, setIsBrandBarChartModalOpen] = useState(false);
    const [isCategoryBarChartModalOpen, setIsCategoryBarChartModalOpen] = useState(false);
    const [isHorizontalBarChartModalOpen, setIsHorizontalBarChartModalOpen] = useState(false);
    const [isPieChartModalOpen, setIsPieChartModalOpen] = useState(false);
    const [isLinkoutListModalOpen, setIsLinkoutListModalOpen] = useState(false);
    const [isBrowserModalOpen, setIsBrowserModalOpen] = useState(false);
    const [isOSModalOpen, setIsOSModalOpen] = useState(false);
    const [isDevicesBarChartModalOpen, setIsDevicesBarChartModalOpen] = useState(false);
    const [isAvatarsBarChartModalOpen, setIsAvatarsBarChartModalOpen] = useState(false);
    const currentDate = new Date();

    const [selectedClientTag, setSelectedClientTag] = useState(null);
    const [isClientTagDropdownOpen, setIsClientTagDropdownOpen] = useState(false);

    const startISO = pdfDates.pdfStartDate.toISOString();
    const endISO = pdfDates.pdfEndDate.toISOString();
    const {
        data: clientTags,
        error: clientTagspError,
        isLoading: clientTagsLoading,
    } = useQuery(["clientTags"], () => getClientTags(), {
        enabled: true,
        ...CACHE_QUERY_OPTIONS(),
    });

    const { data: roomOwners, isLoading: roomOwnersLoading } = useQuery(
        ["roomOwners", user.userId],
        () => getRoomOwnersByClientTag(user.userId),
        { enabled: !!user.userId, ...CACHE_QUERY_OPTIONS() }
    );

    const initialClientTag = useMemo(() => {
        if (!roomOwners?.isAccess) return null;
        if (roomOwners.isFullAccess) return "intraverse"; // default for full access
        const uniqueTags = [...new Set(roomOwners.roomOwners?.map((r) => r.clientTag).filter(Boolean))];
        return uniqueTags[0] || null; // first allowed tag for limited access
    }, [roomOwners]);

    useEffect(() => {
        if (initialClientTag) {
            setSelectedClientTag(initialClientTag);
        }
    }, [initialClientTag]);

    const { data: userData, isLoading } = useQuery(
        ["users", startISO, endISO, selectedClientTag],
        () => getTotalCountData(startISO, endISO, selectedClientTag),
        { enabled: !!startISO && !!endISO && !!selectedClientTag }
    );

    const {
        data: linkouts,
        error: linkoutsErr,
        isLoading: linkoutsLoading,
        refetch: refetchLinkouts,
    } = useQuery(
        ["linkouts", startISO, endISO, selectedClientTag],
        () => getLinkoutsData(startISO, endISO, selectedClientTag),
        {
            enabled: !!startISO && !!endISO && !!selectedClientTag,
            ...CACHE_QUERY_OPTIONS(),
        }
    );

    const {
        data: avatarsData,
        error: avatarsErr,
        isLoading: avatarsLoading,
        refetch: refetchAvatars,
    } = useQuery(
        ["avatars", startISO, endISO, selectedClientTag],
        () => getAvatarsData(startISO, endISO, selectedClientTag),
        {
            enabled: !!startISO && !!endISO && !!selectedClientTag,
            ...CACHE_QUERY_OPTIONS(),
        }
    );

    const {
        data: interactions,
        error: interactionsErr,
        isLoading: interactionsLoading,
        refetch: refetchInteractions,
    } = useQuery(
        ["interactions", startISO, endISO, selectedClientTag],
        () => getInteractionData(startISO, endISO, selectedClientTag),
        {
            enabled: !!startISO && !!endISO && !!selectedClientTag,
            ...CACHE_QUERY_OPTIONS(),
        }
    );

    const {
        data: brandsData,
        error: brandsErr,
        isLoading: brandsLoading,
        refetch: refetchBrands,
    } = useQuery(
        ["brands", startISO, endISO, selectedClientTag],
        () => getBrandsData(startISO, endISO, selectedClientTag),
        {
            enabled: !!startISO && !!endISO && !!selectedClientTag,
            ...CACHE_QUERY_OPTIONS(),
        }
    );

    const {
        data: devicesData,
        error: devicesErr,
        isLoading: devicesLoading,
        refetch: refetchDevices,
    } = useQuery(
        ["devices", startISO, endISO, selectedClientTag],
        () => getDevicesData(startISO, endISO, selectedClientTag),
        {
            enabled: !!startISO && !!endISO && !!selectedClientTag,
            ...CACHE_QUERY_OPTIONS(),
        }
    );

    const {
        data: categoriesData,
        error: categoriesErr,
        isLoading: categoriesLoading,
        refetch: refetchCategories,
    } = useQuery(
        ["categories", startISO, endISO, selectedClientTag],
        () => getCategoriesData(startISO, endISO, selectedClientTag),
        {
            enabled: !!startISO && !!endISO && !!selectedClientTag,
            ...CACHE_QUERY_OPTIONS(),
        }
    );

    const isAnyLoading =
        interactionsLoading ||
        avatarsLoading ||
        isLoading ||
        clientTagsLoading ||
        roomOwnersLoading ||
        brandsLoading ||
        devicesLoading ||
        categoriesLoading ||
        linkoutsLoading;

    const allClientTags = useMemo(() => clientTags?.map((c) => ({ tag: c.tag, value: c.tag })) ?? [], [clientTags]);

    const allowedClientTags = useMemo(() => {
        if (!roomOwners?.isAccess) return [];

        if (roomOwners.isFullAccess) return allClientTags;

        const uniqueTags = [...new Set(roomOwners.roomOwners?.map((r) => r.clientTag).filter(Boolean))];
        return uniqueTags.map((tag) => ({ tag, value: tag }));
    }, [roomOwners, allClientTags]);

    useEffect(() => {
        if (categoriesData) {
            setCategoryData(categoriesData.tagCounts);
        }
        if (linkouts) {
            setLinkoutData(linkouts.linkoutsData);
        }
        if (interactions) {
            setInteractionData(interactions.data);
        }
        if (devicesData) {
            setBrowsersData(devicesData?.browsers ?? []);
            setOSData(devicesData?.os ?? []);
            setDevicesData(devicesData?.devices ?? []);
        }
        if (avatarsData) {
            setAvatarsData(avatarsData.data);
        }
    }, [categoriesData, interactions, linkouts, devicesData, avatarsData]);

    return (
        <>
            {isAnyLoading && (
                <LoadingDiv>
                    <LoadingIcon />
                </LoadingDiv>
            )}

            {!isAnyLoading && !roomOwners?.isAccess && (
                <NoAccessMessage>You do not have access to view this content.</NoAccessMessage>
            )}

            {roomOwners?.isAccess && (
                <AnalyticsContainer>
                    <AnalyticsHeader>
                        <HeaderText>
                            <UserTitle>Hi {username},</UserTitle>
                            {!isAnyLoading && (
                                <AnalyticsText fontSize="18px" fontWeight="600">
                                    Explore insights from <span>All Experiences of {selectedClientTag}.</span>{" "}
                                </AnalyticsText>
                            )}
                        </HeaderText>

                        <FilterContainer>
                            <FilterDropdownWrapper onClick={() => setIsDateFilterOpen(!isDateFilterOpen)}>
                                <Filter mobileWidth={"100%"} ref={dropdownRef}>
                                    <ImageContainer src={FilterIcon} width="16px" height="16px" padding="2.235px 0px" />
                                    <AnalyticsText
                                        fontSize="18px"
                                        fontWeight="600"
                                        highlight={isDateFilterOpen}
                                        style={{ flex: "1", textAlign: "center" }}
                                    >
                                        {selectedDate === "Custom" &&
                                        (!customDateRange.startDate || !customDateRange.endDate)
                                            ? "MMM DD, YYYY to MMM DD, YYYY"
                                            : displayDate}
                                    </AnalyticsText>
                                    <ImageContainer
                                        src={Chevron}
                                        width="16px"
                                        height="16px"
                                        padding="2.235px 0px"
                                        isChevron
                                        // isFlagOpen={isDateFilterOpen}
                                        // onClick={() => setIsDateFilterOpen(!isDateFilterOpen)}
                                    />
                                </Filter>

                                {isDateFilterOpen && !showCustomPicker && (
                                    <DateFilter
                                        selected={selectedDate}
                                        onSelect={(option) => {
                                            setSelectedDate(option);
                                            if (option === "Custom") {
                                                setIsDateFilterOpen(false);
                                                setShowCustomPicker(true);
                                            } else {
                                                setIsDateFilterOpen(false);
                                                setCustomDateRange({ startDate: null, endDate: null });
                                            }
                                        }}
                                        setIsDateFilterOpen={setIsDateFilterOpen}
                                    />
                                )}
                                {showCustomPicker && (
                                    <CustomDatePicker
                                        onChevronClick={() => {
                                            setShowCustomPicker(false);
                                            setIsDateFilterOpen(true);
                                        }}
                                        onDateChange={(start, end) => {
                                            setCustomDateRange({ startDate: start, endDate: end });
                                            setSelectedDate("Custom");
                                            setShowCustomPicker(false);
                                        }}
                                    />
                                )}
                            </FilterDropdownWrapper>

                            {!isAnyLoading && allowedClientTags.length > 1 && (
                                <ClientTagFilter
                                    clientTags={allowedClientTags}
                                    selectedClientTag={selectedClientTag}
                                    setSelectedClientTag={setSelectedClientTag}
                                    isClientTagDropdownOpen={isClientTagDropdownOpen}
                                    setIsClientTagDropdownOpen={setIsClientTagDropdownOpen}
                                />
                            )}
                        </FilterContainer>
                    </AnalyticsHeader>

                    <MetricCardContainer>
                        <MetricCard
                            cardValue={String(userData?.totalIntraverseUsers ?? "0")}
                            cardType="New Users"
                            increaseRate={String(userData?.totalIntraverseUsersIncrease ?? "0%")}
                        />

                        <MetricCard
                            cardValue={String(userData?.totalLogins ?? "0")}
                            cardType="Total Logins"
                            increaseRate={String(userData?.totalLoginsIncrease ?? "0%")}
                        />
                        <MetricCard
                            cardValue={String(userData?.uniqueLogins ?? "0")}
                            cardType="Unique Logins"
                            increaseRate={String(userData?.uniqueLoginsIncrease ?? "0%")}
                        />
                        <MetricCard
                            cardValue={String(userData?.averageSessionMinutes ?? "0")}
                            cardType="Average Session"
                            increaseRate={String(userData?.averageSessionRate ?? "0%")}
                        />
                        <MetricCard
                            cardValue={String(userData?.roomsCompleted ?? "0")}
                            cardType="Completed 5+ Rooms"
                            increaseRate={String(userData?.roomsCompletedRate ?? "0%")}
                        />
                    </MetricCardContainer>

                    <DataContainer>
                        <ChartContainer>
                            <Barchart
                                chartTitle={"Visits Per Brand/Room"}
                                data={brandsData?.allBrandsByTotalVisits ?? []}
                                totalVisit={brandsData?.totalVisits ?? 0}
                                Filter={Filter}
                                ImageContainer={ImageContainer}
                                displayDate={displayDate}
                                isBarChartModalOpen={isBrandBarChartModalOpen}
                                setIsBarChartModalOpen={setIsBrandBarChartModalOpen}
                                pdfStartDate={startISO}
                                pdfEndDate={endISO}
                                client={selectedClientTag}
                            />
                        </ChartContainer>
                        <ChartContainer>
                            <AvatarBarchart
                                chartTitle="Avatar Picks"
                                data={AvatarsData}
                                totalVisit={avatarsData?.totalVisit ?? 0}
                                Filter={Filter}
                                ImageContainer={ImageContainer}
                                displayDate={displayDate}
                                isBarChartModalOpen={isAvatarsBarChartModalOpen}
                                setIsBarChartModalOpen={setIsAvatarsBarChartModalOpen}
                                pdfStartDate={startISO}
                                pdfEndDate={endISO}
                                client={selectedClientTag}
                            />
                        </ChartContainer>
                    </DataContainer>

                    <DataContainer>
                        <ChartContainer>
                            <Piechart
                                data={InteractionData}
                                chartTitle={"Interaction Click Distribution"}
                                isFilter
                                totalVisit={interactions?.totalVisit ?? 0}
                                displayDate={displayDate}
                                isPieChartModalOpen={isPieChartModalOpen}
                                setIsPieChartModalOpen={setIsPieChartModalOpen}
                                headerTextKey={"Interaction"}
                                headerTextValue={"% Of Interaction Clicks"}
                                pdfStartDate={startISO}
                                pdfEndDate={endISO}
                                client={selectedClientTag}
                            />
                        </ChartContainer>
                        <ChartContainer>
                            <HorizontalBarChart
                                chartTitle={"Linkout Click Distribution"}
                                data={LinkoutData}
                                totalVisit={linkouts?.totalVisits ?? 0}
                                Filter={Filter}
                                ImageContainer={ImageContainer}
                                displayDate={displayDate}
                                isHorizontalBarChartModalOpen={isHorizontalBarChartModalOpen}
                                setIsHorizontalBarChartModalOpen={setIsHorizontalBarChartModalOpen}
                                pdfStartDate={startISO}
                                pdfEndDate={endISO}
                                client={selectedClientTag}
                            />
                        </ChartContainer>
                    </DataContainer>

                    <DataContainer>
                        <ChartContainer>
                            <Barchart
                                chartTitle={"Visits Per Category"}
                                data={CategoryData}
                                totalVisit={categoriesData?.totalVisits ?? 0}
                                Filter={Filter}
                                ImageContainer={ImageContainer}
                                displayDate={displayDate}
                                isBarChartModalOpen={isCategoryBarChartModalOpen}
                                setIsBarChartModalOpen={setIsCategoryBarChartModalOpen}
                                pdfStartDate={startISO}
                                pdfEndDate={endISO}
                                client={selectedClientTag}
                            />
                        </ChartContainer>
                        <ChartContainer>
                            <HorizontalBarChart
                                chartTitle={"Devices Breakdown"}
                                data={DevicesData}
                                totalVisit={devicesData?.totalVisit ?? 0}
                                Filter={Filter}
                                ImageContainer={ImageContainer}
                                displayDate={displayDate}
                                isHorizontalBarChartModalOpen={isDevicesBarChartModalOpen}
                                setIsHorizontalBarChartModalOpen={setIsDevicesBarChartModalOpen}
                                type={"devices"}
                                pdfStartDate={startISO}
                                pdfEndDate={endISO}
                                client={selectedClientTag}
                            />
                        </ChartContainer>
                    </DataContainer>
                    <DataContainer>
                        <ChartContainer>
                            <Piechart
                                data={OSData}
                                chartTitle={"Operating System Breakdown"}
                                isFilter
                                totalVisit={devicesData?.totalVisit ?? 0}
                                displayDate={displayDate}
                                isPieChartModalOpen={isOSModalOpen}
                                setIsPieChartModalOpen={setIsOSModalOpen}
                                headerTextKey={"Operating System"}
                                headerTextValue={"% Of OS"}
                                pdfStartDate={startISO}
                                pdfEndDate={endISO}
                                client={selectedClientTag}
                            />
                        </ChartContainer>
                        <ChartContainer>
                            <Piechart
                                data={BrowsersData}
                                chartTitle={"Browser Click Distribution"}
                                isFilter
                                totalVisit={devicesData?.totalVisit ?? 0}
                                displayDate={displayDate}
                                isPieChartModalOpen={isBrowserModalOpen}
                                setIsPieChartModalOpen={setIsBrowserModalOpen}
                                headerTextKey={"Browser"}
                                headerTextValue={"% Of Browser Traffic"}
                                pdfStartDate={startISO}
                                pdfEndDate={endISO}
                                client={selectedClientTag}
                            />
                        </ChartContainer>
                    </DataContainer>
                </AnalyticsContainer>
            )}

            <Modal
                isOpen={isAvatarsBarChartModalOpen}
                chartTitle={"Avatar Picks - Detailed View"}
                onClose={() => setIsAvatarsBarChartModalOpen(false)}
            >
                <AvatarBarchart
                    chartTitle="Avatar Picks"
                    data={AvatarsData}
                    totalVisit={avatarsData?.totalVisit ?? 0}
                    Filter={Filter}
                    ImageContainer={ImageContainer}
                    displayDate={displayDate}
                    isBarChartModalOpen={isAvatarsBarChartModalOpen}
                    setIsBarChartModalOpen={setIsAvatarsBarChartModalOpen}
                />
            </Modal>

            <Modal
                isOpen={isBrandBarChartModalOpen}
                chartTitle={"Visits Per Brand - Detailed View"}
                onClose={() => setIsBrandBarChartModalOpen(false)}
            >
                <Barchart
                    data={brandsData?.allBrandsByTotalVisits ?? []}
                    totalVisit={brandsData?.totalVisits ?? 0}
                    Filter={Filter}
                    ImageContainer={ImageContainer}
                    displayDate={displayDate}
                    isBarChartModalOpen={isBrandBarChartModalOpen}
                />
            </Modal>

            <Modal
                isOpen={isCategoryBarChartModalOpen}
                chartTitle={"Visits Per Category - Detailed View"}
                onClose={() => setIsCategoryBarChartModalOpen(false)}
            >
                <Barchart
                    data={CategoryData}
                    totalVisit={categoriesData?.totalVisits ?? 0}
                    Filter={Filter}
                    ImageContainer={ImageContainer}
                    displayDate={displayDate}
                    isBarChartModalOpen={isCategoryBarChartModalOpen}
                />
            </Modal>

            <Modal
                isOpen={isPieChartModalOpen}
                chartTitle={"Interaction Click Distribution - Detailed View"}
                onClose={() => setIsPieChartModalOpen(false)}
            >
                <Piechart
                    data={InteractionData}
                    chartTitle={"Interaction Click Distribution"}
                    isFilter
                    totalVisit={interactions?.totalVisit ?? 0}
                    displayDate={displayDate}
                    isPieChartModalOpen={isPieChartModalOpen}
                    setIsPieChartModalOpen={setIsPieChartModalOpen}
                    headerTextKey={"Interaction"}
                    headerTextValue={"% Of Interaction Clicks"}
                />
            </Modal>

            <Modal
                isOpen={isLinkoutListModalOpen}
                chartTitle={"Interaction Clicks - Detailed View"}
                onClose={() => setIsLinkoutListModalOpen(false)}
            >
                <LinkoutList
                    data={InteractionData}
                    totalVisit={interactions?.totalVisit ?? 0}
                    Filter={Filter}
                    ImageContainer={ImageContainer}
                    displayDate={displayDate}
                    isLinkoutListModalOpen={isLinkoutListModalOpen}
                    setIsLinkoutListModalOpen={setIsLinkoutListModalOpen}
                />
            </Modal>

            <Modal
                isOpen={isHorizontalBarChartModalOpen}
                chartTitle={"Linkout Click Distribution - Detailed View"}
                onClose={() => setIsHorizontalBarChartModalOpen(false)}
            >
                <HorizontalBarChart
                    chartTitle={"Linkout Click Distribution"}
                    data={LinkoutData}
                    totalVisit={linkouts?.totalVisits ?? 0}
                    Filter={Filter}
                    ImageContainer={ImageContainer}
                    displayDate={displayDate}
                    isHorizontalBarChartModalOpen={isHorizontalBarChartModalOpen}
                    setIsHorizontalBarChartModalOpen={setIsHorizontalBarChartModalOpen}
                />
            </Modal>

            <Modal
                isOpen={isBrowserModalOpen}
                chartTitle={"Browser Traffic Distribution - Detailed View"}
                onClose={() => setIsBrowserModalOpen(false)}
            >
                <Piechart
                    data={BrowsersData}
                    chartTitle={"Browser Traffic Distribution"}
                    isFilter
                    totalVisit={devicesData?.totalVisit ?? 0}
                    displayDate={displayDate}
                    isPieChartModalOpen={isBrowserModalOpen}
                    setIsPieChartModalOpen={setIsBrowserModalOpen}
                    headerTextKey={"Browser"}
                    headerTextValue={"% Of Browser Traffic"}
                />
            </Modal>

            <Modal
                isOpen={isOSModalOpen}
                chartTitle={"Operating System Breakdown - Detailed View"}
                onClose={() => setIsOSModalOpen(false)}
            >
                <Piechart
                    data={OSData}
                    chartTitle={"Operating System Breakdown"}
                    isFilter
                    totalVisit={devicesData?.totalVisit ?? 0}
                    displayDate={displayDate}
                    isPieChartModalOpen={isOSModalOpen}
                    setIsPieChartModalOpen={setIsOSModalOpen}
                    headerTextKey={"Operating System"}
                    headerTextValue={"% Of OS"}
                />
            </Modal>

            <Modal
                isOpen={isDevicesBarChartModalOpen}
                chartTitle={"Devices Breakdown - Detailed View"}
                onClose={() => setIsDevicesBarChartModalOpen(false)}
            >
                <HorizontalBarChart
                    chartTitle={"Devices Breakdown"}
                    data={DevicesData}
                    totalVisit={devicesData?.totalVisit ?? 0}
                    Filter={Filter}
                    ImageContainer={ImageContainer}
                    displayDate={displayDate}
                    isHorizontalBarChartModalOpen={isDevicesBarChartModalOpen}
                    setIsHorizontalBarChartModalOpen={setIsDevicesBarChartModalOpen}
                    type={"devices"}
                />
            </Modal>
        </>
    );
};

Analytics.propTypes = {
    // Define prop types if needed in future
};

export default Analytics;

const LoadingDiv = styled.div`
    position: fixed;
    inset: 0; /* top:0; right:0; bottom:0; left:0 */
    display: flex;
    justify-content: center;
    align-items: center;
    background: rgba(0, 0, 0, 0.4); /* optional overlay */
    z-index: 9999;
`;

const AnalyticsContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0px 40px;
    width: 100%;
    gap: 40px;

    @media (max-width: 768px) {
        padding: 30px 0px;
    }
`;

const UserTitle = styled.h1`
    color: var(--Text-Primary, #fbfcff);
    font-family: "Barlow";
    font-size: 30px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    text-transform: capitalize;
`;

const AnalyticsText = styled.p`
    color: ${(props) => (props.highlight ? "#9D75F5" : props.color || "var(--Text-Primary, #fbfcff)")};
    font-family: "Barlow";
    font-size: ${(props) => props.fontSize || "16px"};
    font-style: normal;
    font-weight: ${(props) => props.fontWeight || "400"};
    line-height: normal;
    text-transform: capitalize;
    letter-spacing: 0.5px;

    span {
        color: var(--Banners-Yellow, #ffe664);
    }
`;

const MetricCardContainer = styled.div`
    display: flex;
    align-items: stretch;
    gap: 40px;
    flex-wrap: wrap;
    width: 100%;

    @media (max-width: 1024px) {
        gap: 24px; /* reduce gap for medium screens */
    }

    @media (max-width: 768px) {
        flex-direction: column; /* stack cards vertically */
        gap: 16px;
        width: 100%;
    }
`;

const DataContainer = styled.div`
    display: flex;
    align-items: stretch;
    gap: 40px;
    align-self: stretch;
    width: 100%;

    @media (max-width: 1250px) {
        flex-direction: column;
        gap: 24px;
    }
`;

const ChartContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 40px;
    border-radius: 14px;
    border: 1px solid rgba(190, 174, 255, 0.1);
    background: var(--purple-secondary-10, rgba(157, 117, 245, 0.1));
    box-sizing: border-box;
    padding: 30px 40px;

    @media (max-width: 1250px) {
        gap: 15px;
        padding: 12px;
    }

    @media (max-width: 768px) {
        padding: 10px;
        min-height: auto;
        gap: 10px;
        width: 100%;
    }
`;

const AnalyticsHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex: 1 0 0;

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 20px;
    }
`;

const HeaderText = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 10px;
`;

const FilterContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;

    @media (max-width: 768px) {
        flex-direction: column;
        width: 100%;
    }
`;

const FilterDropdownWrapper = styled.div`
    position: relative;
    display: inline-block;
    cursor: pointer;
    @media (max-width: 768px) {
        width: 100%;
    }
`;

const Filter = styled.div`
    display: flex;
    width: ${(props) => (props.width ? props.width : "auto")};
    padding: 10px 16px;
    align-items: center;
    gap: 16px;
    border-radius: 77px;
    border: ${(props) =>
        props.isFlagOpen
            ? "2px solid var(--Purple-Secondary, #9D75F5)"
            : "1px solid var(--purple-secondary-30, rgba(157, 117, 245, 0.3))"};
    &:hover {
        background-color: rgba(157, 117, 245, 0.1);
    }

    @media (max-width: 768px) {
        width: ${(props) => (props.mobileWidth ? props.mobileWidth : "auto")};
    }
`;
const ImageContainer = styled.img`
    display: flex;
    width: ${(props) => (props.width ? props.width : "14px")};
    height: ${(props) => (props.height ? props.height : "14px")};
    padding: ${(props) => (props.padding ? props.padding : "1px 1px")};
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transform: ${(props) => {
        if (props.isChevron && props.isFlagOpen) return "rotate(90deg)";
        if (props.isChevron) return "rotate(-90deg)";
        return "none";
    }};
    transition: transform 0.2s;
`;

const NoAccessMessage = styled.div`
    color: var(--Text-Primary, #fbfcff);
    font-family: "Barlow";
    font-size: 20px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    text-align: center;
    padding: 20px;
    background-color: rgba(255, 101, 100, 0.1);
    border: 1px solid var(--Error-Red, #ff6564);
    border-radius: 8px;
    margin: 20px 40px;

    @media (max-width: 768px) {
        margin: 20px 10px;
        font-size: 16px;
    }
`;
